import { motion } from "framer-motion";
import { Pulse, ShieldCheck, GameController, Microphone, ArrowDown } from "@phosphor-icons/react";
import { ASSETS } from "@/lib/constants";
import StoreBadges from "@/components/StoreBadges";

const { appIcon: APP_ICON, heroBackground: HERO_BG } = ASSETS;

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-20 overflow-hidden noise"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#050505]" />
        <div className="absolute inset-0 grid-bg opacity-50" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        {/* Status pill */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-3 border border-[#FF2A2A]/50 bg-[#FF2A2A]/10 px-3 py-1.5 mb-10"
          data-testid="hero-status-pill"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF2A2A] opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF2A2A]" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/90">
            Live on iOS · v1.1.5 · 14-day free trial
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Left – Headline + CTA */}
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tighter"
              data-testid="hero-headline"
            >
              Stable<span className="text-[#FF2A2A]">-ish</span>:
              <br />
              <span className="italic font-light text-white/70">The ER &amp; Critical Care</span>
              <br />
              <span className="font-extrabold">Nurse Reference App.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-5 font-display text-xl sm:text-2xl font-bold tracking-tight text-white/85"
              data-testid="hero-tagline"
            >
              The clinical brain{" "}
              <span className="italic font-light text-[#FF2A2A]">
                in your scrub pocket.
              </span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-white/65 leading-relaxed"
              data-testid="hero-subheadline"
            >
              Stable-ish gives ER and ICU nurses fast, plain-language answers,
              36 bedside tools, and your own hospital protocols — in one app
              built for the middle of a shift.
            </motion.p>

            {/* App Store CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 max-w-xl"
              data-testid="hero-cta-block"
            >
              {/* Trial + pricing hook — trial is the headline, the rate
                  sits as a small mono footnote so the number doesn't punch
                  visitors in the face before they've read a single benefit */}
              <div
                className="flex items-start gap-3 mb-5"
                data-testid="hero-trial-badge"
              >
                <span className="relative flex h-2.5 w-2.5 shrink-0 mt-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF2A2A] opacity-70" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#FF2A2A]" />
                </span>
                <span>
                  <span className="block font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                    14 days free
                  </span>
                  <span className="mt-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">
                    then $9.99/mo or $79.99/yr
                  </span>
                </span>
              </div>

              {/* Store badges (Apple + Google Play coming soon) */}
              <StoreBadges testIdPrefix="hero-store" source="hero" />

              {/* Secondary CTA — scrolls to the "What's new in v1.1.5" section */}
              <a
                href="#whats-new"
                data-testid="hero-whats-new-cta"
                className="group mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/70 hover:text-[#FF2A2A] transition-colors"
              >
                <span>See what&rsquo;s new in v1.1.5</span>
                <ArrowDown
                  size={14}
                  weight="bold"
                  className="transition-transform group-hover:translate-y-0.5"
                />
              </a>

              {/* Trust line */}
              <p
                className="mt-4 max-w-xl text-sm text-white/55 leading-relaxed"
                data-testid="hero-trust-line"
              >
                Free to try for 14 days. No ads. Your chat history auto-purges
                on a schedule.
              </p>

              {/* Founding Member framing + availability note */}
              <p
                className="mt-5 font-display text-base sm:text-lg font-bold tracking-tight text-white/85"
                data-testid="hero-availability"
              >
                Lock in the <span className="text-[#FF2A2A]">Founding Member rate</span> — guaranteed for life.
                <span className="block sm:inline sm:ml-2 text-white/55 font-normal italic">
                  iOS only for now · Android coming soon.
                </span>
              </p>

              {/* Professional-audience disclaimer — reinforces "professional
                  reference tool" classification for ad platforms and clarifies
                  intended audience. */}
              <p
                className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45"
                data-testid="hero-professional-disclaimer"
              >
                For licensed healthcare professionals only · not consumer medical advice
              </p>
            </motion.div>

            {/* Trust strip */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 max-w-2xl border-t border-white/10">
              {[
                { i: <ShieldCheck size={16} weight="duotone" />, k: "Source-cited", v: "every answer" },
                { i: <Pulse size={16} weight="duotone" />, k: "ER + Critical Care", v: "every unit" },
                { i: <GameController size={16} weight="duotone" />, k: "Interactive sims", v: "score yourself" },
                { i: <Microphone size={16} weight="duotone" />, k: "Voice + photo", v: "hands-free input" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="pt-4 pr-4"
                  data-testid={`hero-stat-${i}`}
                >
                  <div className="text-[#FF2A2A] mb-2">{s.i}</div>
                  <div className="font-display text-base sm:text-lg font-extrabold leading-tight">
                    {s.k}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right – Phone mockup */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mx-auto w-[280px] sm:w-[320px]"
              data-testid="hero-phone-mockup"
            >
              <PhoneMockup icon={APP_ICON} />
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF2A2A]/80"
              data-testid="hero-mockup-disclaimer"
            >
              // mockup · illustrative content · final app may differ
            </motion.p>
            {/* EKG line behind */}
            <svg
              viewBox="0 0 600 200"
              className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[120%] max-w-[700px] opacity-50 -z-10"
              aria-hidden="true"
            >
              <path
                d="M0 100 L120 100 L150 100 L165 60 L180 140 L200 30 L215 170 L235 100 L600 100"
                fill="none"
                stroke="#FF2A2A"
                strokeWidth="2"
                strokeDasharray="1200"
                strokeDashoffset="0"
                style={{ animation: "ecg-stroke 4s ease-in-out infinite" }}
              />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneMockup({ icon }) {
  return (
    <div className="relative aspect-[9/19] bg-black border border-white/15 rounded-[44px] p-2 shadow-[0_30px_80px_-30px_rgba(255,42,42,0.35)]">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-2xl z-10 border-b border-white/10" />
      <div className="relative h-full w-full bg-[#0a0a0a] rounded-[36px] overflow-hidden border border-white/10">
        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-3 font-mono text-[10px] text-white/70">
          <span>9:41</span>
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-[#FF2A2A] animate-pulse-soft" />
            LIVE
          </span>
        </div>

        {/* App header */}
        <div className="px-4 pt-4 pb-3 flex items-center gap-2 border-b border-white/10">
          <img src={icon} alt="" className="w-7 h-7 rounded" />
          <div className="leading-tight">
            <div className="font-display text-sm font-bold">
              Stable<span className="text-[#FF2A2A]">-ish</span>
            </div>
            <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-white/45">
              ER/ICU assistant
            </div>
          </div>
        </div>

        {/* Chat messages */}
        <div className="p-4 space-y-3 text-[11px]">
          <div className="ml-auto max-w-[80%] bg-[#FF2A2A] text-white px-3 py-2 rounded-[2px]">
            Push dose epi for symptomatic brady — dose?
          </div>
          <div className="max-w-[88%] bg-white/5 border border-white/10 px-3 py-2 rounded-[2px] text-white/85">
            <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-[#FF2A2A] mb-1">
              Push-dose epi · adult
            </span>
            5–20 mcg IV bolus q2–5 min. Mix 1 mL of 1:10,000 (0.1 mg/mL) in
            9 mL NS → 10 mcg/mL.
            <span className="block mt-1 font-mono text-[9px] text-white/40">
              ACLS 2025
            </span>
          </div>
          <div className="max-w-[88%] bg-white/5 border border-white/10 px-3 py-2 rounded-[2px] text-white/85">
            <span className="inline-block w-1.5 h-3 bg-[#FF2A2A] blink align-middle" />
          </div>
        </div>

        {/* Bottom prompt */}
        <div className="absolute bottom-3 left-3 right-3 border border-white/15 bg-black/80 px-3 py-2 flex items-center gap-2">
          <span className="text-white/30 font-mono text-[10px]">
            ask anything · evidence-cited
          </span>
        </div>
      </div>
    </div>
  );
}
